import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HeartHandshake, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ETHNICITIES = ["Black African", "Black Caribbean", "Asian Indian", "Asian Pakistani", "Asian Bangladeshi", "Chinese", "Mixed / Multiple", "Other ethnic group", "Prefer not to say"];

export default function Register() {
    const { register, formatApiErrorDetail } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff", job_title: "", organisation: "", ethnicity: "" });
    const [loading, setLoading] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await register(form);
            toast.success("Account created");
            navigate("/app/dashboard");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="hidden lg:flex flex-col justify-between bg-nhs-ink text-white p-12">
                <Link to="/" className="flex items-center gap-2"><HeartHandshake className="h-6 w-6 text-nhs-teal" /><span className="font-heading font-extrabold text-lg">EquitySupport</span></Link>
                <div>
                    <h2 className="font-heading text-3xl font-bold leading-snug">Join your Trust's confidential support platform.</h2>
                    <p className="mt-4 text-slate-300 max-w-md">Your ethnicity data is optional, encrypted and only ever used in aggregate for equity reporting.</p>
                </div>
                <p className="text-sm text-slate-400">GDPR compliant · Consent-driven</p>
            </div>

            <div className="flex items-center justify-center p-6 bg-background overflow-y-auto">
                <div className="w-full max-w-md py-6">
                    <h1 className="font-heading text-2xl font-bold text-nhs-ink">Create account</h1>
                    <p className="text-sm text-slate-500 mt-1">Register with your NHS details.</p>

                    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
                        <div>
                            <Label htmlFor="name">Full name</Label>
                            <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="register-name-input" />
                        </div>
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input id="email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@nhs.net" className="mt-1.5" data-testid="register-email-input" />
                        </div>
                        <div>
                            <Label htmlFor="password">Password</Label>
                            <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" data-testid="register-password-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>I am</Label>
                                <Select value={form.role} onValueChange={(v) => set("role", v)}>
                                    <SelectTrigger className="mt-1.5" data-testid="register-role-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="staff">BAME Staff member</SelectItem>
                                        <SelectItem value="hr_leadership">HR / Leadership</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="job">Job title</Label>
                                <Input id="job" value={form.job_title} onChange={(e) => set("job_title", e.target.value)} className="mt-1.5" data-testid="register-job-input" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="org">Organisation / Trust</Label>
                            <Input id="org" value={form.organisation} onChange={(e) => set("organisation", e.target.value)} className="mt-1.5" data-testid="register-org-input" />
                        </div>
                        <div>
                            <Label>Ethnicity (optional)</Label>
                            <Select value={form.ethnicity} onValueChange={(v) => set("ethnicity", v)}>
                                <SelectTrigger className="mt-1.5" data-testid="register-ethnicity-select"><SelectValue placeholder="Prefer not to say" /></SelectTrigger>
                                <SelectContent>
                                    {ETHNICITIES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button type="submit" disabled={loading} className="w-full bg-nhs-blue hover:bg-nhs-darkblue text-white" data-testid="register-submit-button">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
                        </Button>
                    </form>

                    <p className="mt-6 text-sm text-slate-500 text-center">Already registered? <Link to="/login" className="text-nhs-blue font-semibold" data-testid="to-login">Sign in</Link></p>
                </div>
            </div>
        </div>
    );
}
